// Although the organization context has the document configuration, it can be hard for React to deal with state u
// pdates of very nested data. Therefore, we have a separate context for accessing and updating the pdf template configuration.

import { createContext, useContext, useEffect, useState } from "react";
import {
	PDFConfigPlaceholder,
	Region,
	SammelrechnungTitleDateMode,
} from "../types/enums";
import { useDatabaseFunctionsWithPromise } from "../hooks/useDatabase";
import { useToast } from "./ToastContext";
import { PdfTemplateType } from "../pdf-templates/document-template.types";
import { useCentralStore } from "../store/Central";
import { showNotification } from "../store/Central/selectors";
import { Alignment } from "../pdf-templates/pdf-templates/types";
import { supabase } from "@/lib/supabase";
import {
	SupabaseReadTableEnum,
	SupabaseTableEnum,
	SupabaseWriteTableEnum,
} from "@/lib/supabase/supabaseTypes";

export const convertLegacyPdfTemplateConfiguration = (
	config: PdfTemplateConfiguration
): PdfTemplateConfiguration => {
	const convertToRtf = (value: string | RTF | undefined): RTF => {
		if (!value) return { text: "", width: 0 };
		if (typeof value === "string") return { text: value, width: 0 };
		return value;
	};

	const newConfig = { ...config };

	// If the pdf template configuration is legacy with footerText, commentText, addressRtfText, idRtfText being strings, convert them to objects with text and width
	if (
		typeof newConfig[PdfTemplateType.KOSTENVORANSCHLAG].footerText ===
		"string"
	) {
		const templateTypes = [
			...Object.values(PdfTemplateType),
			"general",
		] as const;
		templateTypes.forEach((templateType) => {
			const template = newConfig[templateType];
			if (!template) return;

			if ("footerText" in template) {
				template.footerText = convertToRtf(template.footerText as any);
			}

			if ("commentText" in template) {
				template.commentText = convertToRtf(
					template.commentText as any
				);
			}

			if ("addressRtfText" in template) {
				template.addressRtfText = convertToRtf(
					template.addressRtfText as any
				);
			}

			if ("idRtfText" in template) {
				template.idRtfText = convertToRtf(template.idRtfText as any);
			}
		});

		return newConfig;
	}

	if (!newConfig["general"]?.size) {
		newConfig["general"] = {
			...newConfig["general"],
			size: "lg",
		};
	}

	// logoPath was replaced by logImgurl
	if ("logoPath" in newConfig.general) {
		delete newConfig.general.logoPath;
	}

	return newConfig;
};

export interface RTF {
	text: string;
	width: number;
}

export interface ArticleFields {
	showAdditionalText?: boolean;
	showManufacturer?: boolean;
	showCeNumber?: boolean;
	showComposition?: boolean;
	showLotNumber?: boolean;
}

export interface CommonProps {
	addressAlignment?: Alignment;
	oneLiner?: boolean;
	showBorder?: boolean;
	recipientAddressAlignment?: Alignment;
	recipientAddressTopGap?: number;
	recipientAddressRightGap?: number;
	recipientAddressFontSize?: "sm" | "md" | "lg" | "xl" | "xxl";
	senderAddressFontSize?: "sm" | "md" | "lg" | "xl" | "xxl";
	// Shifts the sender/letterhead address block up by this many mm (moves it closer to
	// the top edge). Undefined keeps the legacy rendering untouched.
	senderAddressUpShift?: number;
	senderAddressAboveRecipient?: boolean;
	idOneLiner?: boolean;
	idAlignment?: Omit<Alignment, "center">;
	showSwissLabel?: boolean;
	showSZVLabel?: boolean;
	organizationsImgAlignment?: Omit<Alignment, "center">;
	organizationsImgPosition?: "top" | "bottom";
	orgImgText?: string;
	customIconPath?: string;
	customImgUrl?: string;
	showVzlsMembership?: boolean;
	addressRtfEnabled?: boolean;
	addressRtfText?: RTF;
	idRtfEnabled?: boolean;
	idRtfText?: RTF;
	absoluteRecipientAddress?: boolean;
	hideLabAddress?: boolean;
	hideMembers?: boolean;
	hideIds?: boolean;
	headerBodyGap?: number;
	customFooterEnabled?: boolean;
	idNumbersGap?: number;
	backgroundImageEnabled?: boolean;
	backgroundImageFirstPageOnly?: boolean;
	// Document-specific overrides for general formatting options
	overrideShowTotalCurrency?: boolean;
	// Region-specific comment overrides, keyed by Region (CH/DE). When a region has
	// no entry, the document falls back to the single `commentText` of the template.
	commentTextByRegion?: Partial<Record<Region, RTF>>;
}

export interface PdfTemplateConfiguration {
	general: CommonProps & {
		clientAddressLeft: boolean;
		email: string;
		website: string;
		marginTop: number;
		marginBottom: number;
		marginLeft: number;
		marginRight: number;
		logoImgurl?: string;
		logoSize?: number;
		logoAlignment?: Alignment;
		footerAlignment: Alignment;
		commentAlignment: Alignment;
		size?: "sm" | "md" | "lg";
		dateFormat?: "short" | "long"; // "short" = 7.7.2026 (default), "long" = 7. Juli 2026
		tableDensity?: "sm" | "md" | "lg"; // Table row height density
		tableBackgroundColor?: string; // Background color for table headers in PDFs
		// Bold formatting options
		isTotalBold?: boolean;
		isPatientBold?: boolean;
		isPractitionerBold?: boolean;
		// VAT display options
		showZeroVat?: boolean;
		// Currency display options
		showTotalCurrency?: boolean;
		// QR payment slip rendering
		qrSlipOnExtraPage?: boolean;
		// Custom footer using page templates
		customFooterType?: "text" | "image" | "memberships";
		customFooterText?: string;
		customFooterImageUrl?: string;
		customFooterHeight?: number; // Height in mm
		customFooterAlignment?: Alignment;
		customFooterIgnoreLayout?: boolean;
		customFooterOnlyFirstPage?: boolean;
		// Image sizing options
		customFooterImageWidth?: "auto" | "full" | "custom";
		customFooterImageCustomWidth?: number; // Width in mm
		// Background image
		backgroundImageUrl?: string;
		backgroundImageEnabled?: boolean;
		backgroundImageFirstPageOnly?: boolean;
	};
	[PdfTemplateType.KOSTENVORANSCHLAG]: CommonProps & {
		footerText: RTF;
		showSignature: boolean;
		showLogo: boolean;
		commentText?: RTF;
		articleFields?: ArticleFields;
	};
	[PdfTemplateType.LIEFERSCHEIN]: CommonProps & {
		footerText: RTF;
		showLogo: boolean;
		commentText?: RTF;
		showNotesInTable?: boolean;
		taxAmountText?: string;
		showZazInclusiveAmounts?: boolean;
		articleFields?: ArticleFields;
	};
	[PdfTemplateType.RECHNUNG]: CommonProps & {
		footerText: RTF;
		textAbovePaymentSlip: string;
		commentText?: RTF;
		showLogo: boolean;
		taxAmountText?: string;
		showZazInclusiveAmounts?: boolean;
		articleFields?: ArticleFields;
	};
	[PdfTemplateType.RECHNUNGSDOPPEL]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
	};
	[PdfTemplateType.ZAHLUNGSERINNERUNG]: CommonProps & {
		footerText: RTF;
		showLogo: boolean;
		commentText?: RTF;
		title: string;
		bodyText: string;
	};
	[PdfTemplateType.ERSTE_MAHNUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		title: string;
		bodyText: string;
	};
	[PdfTemplateType.FOLGEMAHNUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		title: string;
		bodyText: string;
	};
	[PdfTemplateType.LETZTE_MAHNUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		title: string;
		bodyText: string;
	};
	[PdfTemplateType.BARZAHLUNGSQUITTUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
	};
	[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
	};
	[PdfTemplateType.AKONTORECHNUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		textAbovePaymentSlip: string;
	};
	[PdfTemplateType.GUTSCHRIFT]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		articleFields?: ArticleFields;
	};
	[PdfTemplateType.AUFTRAGSBLATT]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		articleFields?: ArticleFields;
	};
	[PdfTemplateType.ETIK_AUFTRAG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		showToothColor?: boolean;
	};
	[PdfTemplateType.SAMMELRECHNUNG]: CommonProps & {
		footerText: RTF;
		commentText?: RTF;
		showLogo: boolean;
		taxAmountText?: string;
		showZazInclusiveAmounts?: boolean;
		titleDateMode?: SammelrechnungTitleDateMode;
		textAbovePaymentSlip: string;
	};
}

const ZAHLUNGSERINNERUNG_BODY = `#ANREDE

Möglicherweise haben Sie in der Hektik des Alltages unsere Rechnung Nr. #RGNR vom #RGDATUM in Höhe von #OFFEN völlig übersehen. Wir bitten Sie, den Betrag bis zum #FRIST zu überweisen.

Sollten Sie bereits überwiesen haben, so entschuldigen Sie bitte die Überschneidung.

Mit freundlichen Grüssen`;

const ERSTE_MAHNUNG_BODY = `#ANREDE

Da unsere Zahlungserinnerung unbeantwortet geblieben ist, bitten wir Sie, den offenen Betrag von #TOTAL bis zum #FRIST zu begleichen oder uns umgehend anzurufen.

Sollte sich unser Schreiben mit Ihrer Zahlung gekreuzt haben, so betrachten Sie diese Mahnung als gegenstandslos.

Mit freundlichen Grüssen`;

const FOLGEMAHNUNG_BODY = `#ANREDE

Da unsere Mahnungen unbeantwortet geblieben sind, bitten wir Sie, den offenen Betrag von #TOTAL bis zum #FRIST zu begleichen oder uns umgehend anzurufen.

Sollte sich unser Schreiben mit Ihrer Zahlung gekreuzt haben, so betrachten Sie diese Mahnung als gegenstandslos.

Mit freundlichen Grüssen`;

const LETZTE_MAHNUNG_BODY = `#ANREDE

Trotz mehrfacher Mahnung ist der offene Betrag von #TOTAL noch nicht bei uns eingegangen. Wir fordern Sie hiermit letztmals auf, den Betrag bis zum #FRIST zu begleichen.

Sollte die Zahlung bis zu dieser Frist nicht eingehen, sehen wir uns leider gezwungen, ohne weitere Ankündigung die Betreibung einzuleiten.

Mit freundlichen Grüssen`;

export const DEFAULT_TEMPLATE_CONFIGURATION: PdfTemplateConfiguration = {
	general: {
		addressAlignment: "left",
		addressRtfEnabled: false,
		clientAddressLeft: true,
		customIconPath: "",
		footerAlignment: "center",
		commentAlignment: "left",
		customImgUrl: undefined,
		organizationsImgPosition: "top",
		organizationsImgAlignment: "left",
		showSwissLabel: false,
		email: "",
		website: "",
		showVzlsMembership: true,
		marginTop: 5,
		marginBottom: 10,
		marginLeft: 20,
		marginRight: 10,
		idOneLiner: false,
		idRtfEnabled: false,
		idNumbersGap: 0,
		recipientAddressAlignment: "right",
		recipientAddressTopGap: 0,
		recipientAddressRightGap: 0,
		recipientAddressFontSize: "lg",
		senderAddressFontSize: "lg",
		senderAddressAboveRecipient: false,
		size: "lg",
		// Bold formatting defaults
		isTotalBold: false,
		isPatientBold: false,
		isPractitionerBold: false,
		// VAT display defaults
		showZeroVat: false,
		// Currency display defaults
		showTotalCurrency: false,
		// QR payment slip rendering defaults
		qrSlipOnExtraPage: false,
		// Custom footer defaults
		customFooterEnabled: false,
		customFooterType: "text",
		customFooterText: "",
		customFooterImageUrl: "",
		customFooterHeight: 15, // 15mm default height
		customFooterAlignment: "center",
		// Image sizing defaults
		customFooterImageWidth: "full", // Default to full width
		customFooterImageCustomWidth: 100, // 100mm default custom width
		customFooterIgnoreLayout: false,
		customFooterOnlyFirstPage: false,
		// Background image defaults
		backgroundImageEnabled: false,
		backgroundImageUrl: "",
		backgroundImageFirstPageOnly: false,
	},
	[PdfTemplateType.KOSTENVORANSCHLAG]: {
		footerText: {
			text: "Die angegebenen Kosten können bis zu 15% abweichen. Falls zusätzliche Arbeiten notwendig werden, die einen grösseren Mehraufwand bedingen, werden wir Sie darauf aufmerksam machen.",
			width: 180,
		},
		showSignature: true,
		// if general has logo and footer, by default we want to show it on specific templates
		// user can then disable it for specific templates where needed
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
	},
	[PdfTemplateType.LIEFERSCHEIN]: {
		footerText: {
			text: "Wir versichern, dass das Produkt für den oben erwähnten Patienten hergestellt wurde, und dass es den in Anhang I und insbesondere VIII der RL93/42/EWG genannten grundlegenden Anforderungen entspricht.",
			width: 0,
		},
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		taxAmountText: `* Der steuerpflichtige Betrag ergibt ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZTAX}, steuerfrei sind ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZFREE}. (Angaben ${PDFConfigPlaceholder.ZAZTAXSTATUS} und ohne Gewähr)`,
		showZazInclusiveAmounts: false,
	},
	[PdfTemplateType.RECHNUNG]: {
		footerText: { text: "", width: 0 },
		textAbovePaymentSlip: "Zahlbar innert 30 Tagen",
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		taxAmountText: `* Der steuerpflichtige Betrag ergibt ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZTAX}, steuerfrei sind ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZFREE}. (Angaben ${PDFConfigPlaceholder.ZAZTAXSTATUS} und ohne Gewähr)`,
		showZazInclusiveAmounts: false,
	},
	[PdfTemplateType.RECHNUNGSDOPPEL]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
	},
	[PdfTemplateType.ZAHLUNGSERINNERUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		title: "Zahlungserinnerung",
		bodyText: ZAHLUNGSERINNERUNG_BODY,
	},
	[PdfTemplateType.ERSTE_MAHNUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		title: "Mahnung",
		bodyText: ERSTE_MAHNUNG_BODY,
	},
	[PdfTemplateType.FOLGEMAHNUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		title: "Mahnung",
		bodyText: FOLGEMAHNUNG_BODY,
	},
	[PdfTemplateType.LETZTE_MAHNUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		title: "Letzte Mahnung",
		bodyText: LETZTE_MAHNUNG_BODY,
	},
	[PdfTemplateType.BARZAHLUNGSQUITTUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
	},
	[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
	},
	[PdfTemplateType.AKONTORECHNUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		textAbovePaymentSlip: "Zahlbar innert 30 Tagen",
	},
	[PdfTemplateType.GUTSCHRIFT]: {
		footerText: {
			text: "Wir versichern, dass das Produkt für den oben erwähnten Patienten hergestellt wurde, und dass es den in Anhang I der RL93/42/EWG genannten grundlegenden Anforderungen entspricht.",
			width: 0,
		},
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
	},
	[PdfTemplateType.AUFTRAGSBLATT]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		// As Auftragsblatt is an internal document, assume the user doesn't want to show the footer
		customFooterEnabled: false,
		backgroundImageEnabled: true,
	},
	[PdfTemplateType.ETIK_AUFTRAG]: {
		footerText: { text: "", width: 0 },
		showLogo: false,
		showToothColor: false,
	},
	[PdfTemplateType.SAMMELRECHNUNG]: {
		footerText: { text: "", width: 0 },
		showLogo: true,
		customFooterEnabled: true,
		backgroundImageEnabled: true,
		overrideShowTotalCurrency: false,
		taxAmountText: `* Der steuerpflichtige Betrag ergibt ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZTAX}, steuerfrei sind ${PDFConfigPlaceholder.CURRENCY} ${PDFConfigPlaceholder.ZAZFREE}. (Angaben ${PDFConfigPlaceholder.ZAZTAXSTATUS} und ohne Gewähr)`,
		showZazInclusiveAmounts: false,
		titleDateMode: SammelrechnungTitleDateMode.JOB_DATE_RANGE,
		textAbovePaymentSlip: "Zahlbar innert 30 Tagen",
	},
};

// The columns of the sub-configurations that can be updated
type SettingsColumn = PdfTemplateType | "general";

// The columns of each sub-configuration that can be updated (particularly used for resetting the configuration)
type ConfigurationColumnNames =
	| keyof PdfTemplateConfiguration["general"]
	| keyof PdfTemplateConfiguration[PdfTemplateType.KOSTENVORANSCHLAG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.LIEFERSCHEIN]
	| keyof PdfTemplateConfiguration[PdfTemplateType.RECHNUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.RECHNUNGSDOPPEL]
	| keyof PdfTemplateConfiguration[PdfTemplateType.ZAHLUNGSERINNERUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.ERSTE_MAHNUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.FOLGEMAHNUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.LETZTE_MAHNUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.BARZAHLUNGSQUITTUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.AKONTORECHNUNG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.GUTSCHRIFT]
	| keyof PdfTemplateConfiguration[PdfTemplateType.AUFTRAGSBLATT]
	| keyof PdfTemplateConfiguration[PdfTemplateType.ETIK_AUFTRAG]
	| keyof PdfTemplateConfiguration[PdfTemplateType.SAMMELRECHNUNG];

type PartialPdfTemplateConfiguration = Partial<
	| PdfTemplateConfiguration["general"]
	| PdfTemplateConfiguration[PdfTemplateType.KOSTENVORANSCHLAG]
	| PdfTemplateConfiguration[PdfTemplateType.LIEFERSCHEIN]
	| PdfTemplateConfiguration[PdfTemplateType.RECHNUNG]
	| PdfTemplateConfiguration[PdfTemplateType.RECHNUNGSDOPPEL]
	| PdfTemplateConfiguration[PdfTemplateType.ZAHLUNGSERINNERUNG]
	| PdfTemplateConfiguration[PdfTemplateType.ERSTE_MAHNUNG]
	| PdfTemplateConfiguration[PdfTemplateType.FOLGEMAHNUNG]
	| PdfTemplateConfiguration[PdfTemplateType.LETZTE_MAHNUNG]
	| PdfTemplateConfiguration[PdfTemplateType.BARZAHLUNGSQUITTUNG]
	| PdfTemplateConfiguration[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]
	| PdfTemplateConfiguration[PdfTemplateType.AKONTORECHNUNG]
	| PdfTemplateConfiguration[PdfTemplateType.GUTSCHRIFT]
	| PdfTemplateConfiguration[PdfTemplateType.AUFTRAGSBLATT]
	| PdfTemplateConfiguration[PdfTemplateType.ETIK_AUFTRAG]
	| PdfTemplateConfiguration[PdfTemplateType.SAMMELRECHNUNG]
>;

// We export this because it's used in pdf-template-single-configurations.component.tsx
export interface PdfTemplateConfigurationContextProps {
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	updatePdfTemplateConfiguration: (
		settingsColumn: SettingsColumn,
		newConfig: PartialPdfTemplateConfiguration
	) => Promise<boolean>;
	resetConfigurationColumn: (
		settingsColumn: SettingsColumn,
		column: ConfigurationColumnNames
	) => Promise<void>;
}

const PdfTemplateConfigurationContext = createContext<
	PdfTemplateConfigurationContextProps | undefined
>(undefined);

export const PdfTemplateConfigurationContextProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }: any) => {
	const [pdfTemplateConfiguration, setPdfTemplateConfiguration] =
		useState<PdfTemplateConfiguration>(DEFAULT_TEMPLATE_CONFIGURATION);
	const organizationId = useCentralStore((state) => state.organization?.id);
	const { showToast } = useToast();

	const { updateDataWithPromise } = useDatabaseFunctionsWithPromise();
	const { fetchDataWithPromise } = useDatabaseFunctionsWithPromise();

	useEffect(() => {
		if (organizationId) {
			fetchPdfTemplateConfiguration();
		}
	}, [organizationId]);

	const fetchPdfTemplateConfiguration = async () => {
		if (!organizationId) return;

		const { data, error } = await supabase
			.from(SupabaseReadTableEnum.ORGANIZATIONS_VIEW)
			.select("documents_config")
			.eq("id", organizationId)
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Laden der PDF-Einstellungen",
				type: "error",
			});
			return;
		}

		let pdfTemplateConfiguration =
			data.documents_config ?? DEFAULT_TEMPLATE_CONFIGURATION;

		// If the pdf template configuration is legacy with footerText, commentText, addressRtfText, idRtfText being strings, convert them to objects with text and width
		pdfTemplateConfiguration = convertLegacyPdfTemplateConfiguration(
			pdfTemplateConfiguration as PdfTemplateConfiguration
		);

		// In case some fields haven't been set, for every column we merge the default configuration with configuration from the db
		setPdfTemplateConfiguration({
			general: {
				...DEFAULT_TEMPLATE_CONFIGURATION.general,
				...pdfTemplateConfiguration.general,
			},
			[PdfTemplateType.KOSTENVORANSCHLAG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.KOSTENVORANSCHLAG
				],
				...pdfTemplateConfiguration[PdfTemplateType.KOSTENVORANSCHLAG],
			},
			[PdfTemplateType.LIEFERSCHEIN]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.LIEFERSCHEIN],
				...pdfTemplateConfiguration[PdfTemplateType.LIEFERSCHEIN],
			},
			[PdfTemplateType.RECHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.RECHNUNG],
				...pdfTemplateConfiguration[PdfTemplateType.RECHNUNG],
			},
			[PdfTemplateType.RECHNUNGSDOPPEL]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.RECHNUNGSDOPPEL
				],
				...pdfTemplateConfiguration[PdfTemplateType.RECHNUNGSDOPPEL],
			},
			[PdfTemplateType.ZAHLUNGSERINNERUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.ZAHLUNGSERINNERUNG
				],
				...pdfTemplateConfiguration[PdfTemplateType.ZAHLUNGSERINNERUNG],
			},
			[PdfTemplateType.ERSTE_MAHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.ERSTE_MAHNUNG
				],
				...pdfTemplateConfiguration[PdfTemplateType.ERSTE_MAHNUNG],
			},
			[PdfTemplateType.FOLGEMAHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.FOLGEMAHNUNG],
				...pdfTemplateConfiguration[PdfTemplateType.FOLGEMAHNUNG],
			},
			[PdfTemplateType.LETZTE_MAHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.LETZTE_MAHNUNG],
				...pdfTemplateConfiguration[PdfTemplateType.LETZTE_MAHNUNG],
			},
			[PdfTemplateType.BARZAHLUNGSQUITTUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.BARZAHLUNGSQUITTUNG
				],
				...pdfTemplateConfiguration[
					PdfTemplateType.BARZAHLUNGSQUITTUNG
				],
			},
			[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.KARTENZAHLUNGSQUITTUNG
				],
				...pdfTemplateConfiguration[
					PdfTemplateType.KARTENZAHLUNGSQUITTUNG
				],
			},
			[PdfTemplateType.AKONTORECHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.AKONTORECHNUNG
				],
				...pdfTemplateConfiguration[PdfTemplateType.AKONTORECHNUNG],
			},
			[PdfTemplateType.GUTSCHRIFT]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.GUTSCHRIFT],
				...pdfTemplateConfiguration[PdfTemplateType.GUTSCHRIFT],
			},
			[PdfTemplateType.AUFTRAGSBLATT]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.AUFTRAGSBLATT
				],
				...pdfTemplateConfiguration[PdfTemplateType.AUFTRAGSBLATT],
			},
			[PdfTemplateType.ETIK_AUFTRAG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[PdfTemplateType.ETIK_AUFTRAG],
				...pdfTemplateConfiguration[PdfTemplateType.ETIK_AUFTRAG],
			},
			[PdfTemplateType.SAMMELRECHNUNG]: {
				...DEFAULT_TEMPLATE_CONFIGURATION[
					PdfTemplateType.SAMMELRECHNUNG
				],
				...pdfTemplateConfiguration[PdfTemplateType.SAMMELRECHNUNG],
			},
		});
	};

	/**
	 * updatePdfTemplateConfiguration - updates the document config column in the organizations table
	 * @param newConfig - the new config to be saved
	 * @returns
	 */
	const updatePdfTemplateConfiguration = async (
		settingsColumn: SettingsColumn,
		newConfig: PartialPdfTemplateConfiguration
	) => {
		let updatedConfig;
		setPdfTemplateConfiguration((prev) => {
			updatedConfig = {
				...prev,
				[settingsColumn]: {
					...prev[settingsColumn],
					...newConfig,
				},
			};
			return updatedConfig;
		});

		if (!organizationId) return false;
		const { data, error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({
				documents_config: updatedConfig as any,
			})
			.eq("id", organizationId);

		if (error) {
			showToast("Fehler beim Speichern der Einstellungen", "error");
			return false;
		}
		return true;
	};

	/**
	 * Resets a document configuration to the default values
	 * @param settingsColumn - the column to be reset
	 * @param column - the column to be reset
	 */
	const resetConfigurationColumn = async (
		settingsColumn: SettingsColumn,
		column: ConfigurationColumnNames
	) => {
		updatePdfTemplateConfiguration(settingsColumn, {
			[column]: (DEFAULT_TEMPLATE_CONFIGURATION[settingsColumn] as any)[
				column
			],
		});
	};

	return (
		<PdfTemplateConfigurationContext.Provider
			value={{
				pdfTemplateConfiguration,
				updatePdfTemplateConfiguration,
				resetConfigurationColumn,
			}}
		>
			{children}
		</PdfTemplateConfigurationContext.Provider>
	);
};

/**
 * usePdfTemplateConfigurationContext - returns the document configuration context
 * @example const { pdfTemplateConfiguration, updatePdfTemplateConfiguration } = usePdfTemplateConfigurationContext();
 */
export const usePdfTemplateConfigurationContext = () => {
	const context = useContext(PdfTemplateConfigurationContext);
	if (!context) {
		throw new Error(
			"usePdfTemplateConfiguration must be used within pdfTemplateConfigurationurationProvider"
		);
	}
	return context;
};
